import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useGlobal from "../../../../state/useGlobal";
import api from "../../../../services/api";
import {HandleAPICartError} from "../../../../helpers";
import AdyenCheckout from "@adyen/adyen-web";
import GlobalLockLoading from "../../../../components/GlobalLockLoading";
import {checkPaymentStatusAdyen, getDefaultConfigAdyen} from "../adyenHelper";
import {HandleSuccessOrder} from "../../../../services/helper";

function getOS() {
    let userAgent = window.navigator.userAgent,
        platform = window.navigator?.userAgentData?.platform || window.navigator.platform,
        macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
        windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
        iosPlatforms = ['iPhone', 'iPad', 'iPod'],
        os = null;

    if (macosPlatforms.indexOf(platform) !== -1) {
        os = 'Mac OS';
    } else if (iosPlatforms.indexOf(platform) !== -1) {
        os = 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
        os = 'Windows';
    } else if (/Android/.test(userAgent)) {
        os = 'Android';
    } else if (/Linux/.test(platform)) {
        os = 'Linux';
    }

    return os;
}

export default function GoogleApplePayment() {
    /* eslint-disable no-unused-vars */
    const [globalState, globalActions] = useGlobal();
    const {groupOrder, checkout} = globalState;


    const {school_code} = groupOrder;
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const initializedRef = useRef(false);
    const paymentComponentRef = useRef(null);
    // Tracks whether an Apple Pay / Google Pay session is already in flight, so a
    // rapid double-tap cannot start a second session. WebKit allows only one
    // active ApplePaySession per page; a second begin() throws
    // "Page already has an active payment session" (InvalidAccessError).
    const sessionActiveRef = useRef(false);

    const _placeOrder = async (result) => {

        setLoading(true);

        const {checkout} = globalState;
        const {quote_id, email} = checkout;

        try {
            const payload = {
                cartId: quote_id,
                email: email,
                paymentMethod: {
                    method: "adyen_hpp",
                    additional_data: {
                        'stateData': JSON.stringify(result.data),
                        'guestEmail': email,
                        'isGooglePayAndApplePay': true
                    }
                },
            };

            let orderResponse = await api.post(
                `/guest-carts/${quote_id}/payment-information`,
                {},
                payload
            );

            let orderId = orderResponse.data;
            let response = await checkPaymentStatusAdyen(orderId);

            if (response.isFinal) {
                HandleSuccessOrder(globalState, globalActions, orderId);
            } else {
                //Can be 3ds here
                throw Error('Payment is not final from Adyen side');
            }
        } catch (error) {
            HandleAPICartError(error, globalState);
        }
        setLoading(false);
        // Session finished (authorized and submitted); allow a new tap.
        sessionActiveRef.current = false;
    };


    useEffect(() => {
        // Guard against double-initialization (React StrictMode / re-mounts).
        // A second Adyen instance would create a second Apple Pay button and,
        // on tap, call ApplePaySession.begin() while the first session is still
        // active, throwing "Page already has an active payment session".
        if (initializedRef.current) {
            return;
        }
        initializedRef.current = true;

        const createCheckout = async () => {
            let adyenCheckout = await (new AdyenCheckout(getDefaultConfigAdyen(globalState, {
                onSubmit: _placeOrder
            })));

            try {
                if (getOS() === 'iOS' || getOS() === 'Mac OS') {
                    const applyPayComponent = adyenCheckout.create('applepay', {
                        // Gate the session start. Adyen creates a fresh
                        // ApplePaySession and calls begin() only after onClick
                        // resolves, so rejecting here while a session is already
                        // in flight stops a second begin() (the source of "Page
                        // already has an active payment session"). The flag is
                        // cleared whenever the session ends: cancel, error, or
                        // after the authorized payment is submitted (_placeOrder).
                        onClick: (resolve, reject) => {
                            if (sessionActiveRef.current) {
                                reject();
                                return;
                            }
                            sessionActiveRef.current = true;
                            resolve();
                        },
                        onCancel: () => {
                            sessionActiveRef.current = false;
                        },
                        onError: (error) => {
                            sessionActiveRef.current = false;
                            HandleAPICartError(error, globalState);
                        },
                        amount: {
                            currency: checkout.payment_methods_adyen.paymentMethodsExtraDetails['applepay'].configuration.amount.currency, value: checkout.payment_methods_adyen.paymentMethodsExtraDetails['applepay'].configuration.amount.value,
                        }
                    });
                    paymentComponentRef.current = applyPayComponent;
                    applyPayComponent
                        .isAvailable()
                        .then(() => {
                            applyPayComponent.mount("#applepay-container");
                        })
                        .catch(e => {
                            HandleAPICartError(e, globalState);
                        });
                } else {
                    const googlePayComponent = adyenCheckout.create('googlepay', {
                        buttonSizeMode: "fill", buttonType: "checkout", amount: {
                            currency: checkout.payment_methods_adyen.paymentMethodsExtraDetails['googlepay'].configuration.amount.currency, value: checkout.payment_methods_adyen.paymentMethodsExtraDetails['googlepay'].configuration.amount.value,
                        }
                    });
                    paymentComponentRef.current = googlePayComponent;
                    googlePayComponent
                        .isAvailable()
                        .then(() => {
                            googlePayComponent.mount("#googlepay-container");
                        })
                        .catch(e => {
                            HandleAPICartError(e, globalState);
                        });
                }
            } catch (e) {
                HandleAPICartError(e, globalState);
            }
        }

        createCheckout();

        return () => {
            // Unmount the Adyen component on teardown so any active Apple Pay
            // session is aborted, otherwise the next mount collides with it.
            if (paymentComponentRef.current) {
                try {
                    paymentComponentRef.current.unmount();
                } catch (e) {
                    // ignore teardown errors
                }
                paymentComponentRef.current = null;
            }
            initializedRef.current = false;
        };
    }, []); //eslint-disable-line react-hooks/exhaustive-deps


    return (
        loading ? (<GlobalLockLoading/>) : (<>
            <div>
                <div id="googlepay-container"></div>
                <div id="applepay-container"></div>
            </div>
        </>)
    );
}
